import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getFerrySchedulesCached } from '../../../utils/ferryApi';

const FerryScheduleSelector = ({ selectedFerry, onFerryChange, departureDate, onDepartureDateChange, ferryError, dateError }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [selectedPort, setSelectedPort] = useState('fromentine');
  const [refreshToken, setRefreshToken] = useState(0); // pour forcer le rechargement

  // Liste des ports disponibles (conservée localement car métadonnées UI)
  const ports = [
    { value: 'fromentine', label: 'Fromentine' },
    { value: 'saint-gilles', label: 'Saint-Gilles' },
    { value: 'barbatre', label: 'Barbâtre' }
  ];

  const [schedules, setSchedules] = useState([]);
  const [apiErrors, setApiErrors] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!departureDate || !selectedPort) { setSchedules([]); return; }
      setIsLoading(true);
      setApiErrors([]);
      try {
        const { schedules: apiSchedules, errors } = await getFerrySchedulesCached({ date: departureDate, port: selectedPort });
        if (cancelled) return;
        setSchedules(apiSchedules);
        setApiErrors(errors || []);
      } catch (e) {
        if (!cancelled) setApiErrors([{ company: 'all', message: e.message }]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [departureDate, selectedPort, refreshToken]);

  const refreshSchedules = async () => {
    if (!departureDate || !selectedPort) return;
    setIsLoading(true);
    try {
      const { schedules: apiSchedules, errors } = await getFerrySchedulesCached({ date: departureDate, port: selectedPort }, { force: true });
      setSchedules(apiSchedules);
      setApiErrors(errors || []);
      setLastUpdated(new Date());
    } catch (e) {
      setApiErrors([{ company: 'all', message: e.message }]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-success';
      case 'delayed': return 'text-warning';
      case 'cancelled': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return 'CheckCircle';
      case 'delayed': return 'Clock';
      case 'cancelled': return 'XCircle';
      default: return 'Circle';
    }
  };

  const formatTime = (time) => {
    return new Date(`2024-01-01T${time}:00`)?.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const today = new Date()?.toISOString()?.split('T')?.[0];

  return (
    <div className="bg-card rounded-lg p-6 maritime-shadow-sm border border-border">
      {/* Date Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground mb-2">Date de traversée</label>
        <input
          type="date"
          value={departureDate}
          min={today}
          onChange={(e) => onDepartureDateChange(e?.target?.value)}
          className={`w-full h-10 px-3 rounded-md border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary maritime-transition ${dateError ? 'border-destructive focus:ring-destructive' : 'border-input'}`}
          required
        />
        {dateError && (
          <p className="text-xs text-destructive mt-1">{dateError}</p>
        )}
      </div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Ship" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Horaires des ferries</h3>
            <p className="text-sm text-muted-foreground">
              Dernière mise à jour: {lastUpdated?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshSchedules}
          loading={isLoading}
          iconName="RefreshCw"
          iconPosition="left"
          iconSize={16}
        >
          Actualiser
        </Button>
      </div>

      {/* Port Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground mb-3">Choisissez votre port de départ</label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {ports?.map((port) => (
            <button
              key={port?.value}
              type="button"
              onClick={() => setSelectedPort(port?.value)}
              className={`p-4 rounded-lg border-2 maritime-transition text-center ${
                selectedPort === port?.value
                  ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 text-foreground'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Icon name="MapPin" size={18} />
                <span className="font-medium">{port?.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Ferry Schedule List */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-foreground">
          Vers l'Île d'Yeu depuis {ports?.find(p => p?.value === selectedPort)?.label}
        </label>
        
        {isLoading && schedules.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">Chargement des horaires...</div>
        )}
        {!isLoading && schedules.length === 0 && apiErrors.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">Aucun horaire disponible pour cette date.</div>
        )}
        {schedules?.map((ferry) => {
          const isSelected = selectedFerry?.id === ferry?.id;
          return (
            <div
              key={ferry?.id}
              onClick={() => onFerryChange(ferry)}
              className={`p-4 rounded-lg border-2 cursor-pointer maritime-transition ${
                isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-foreground">{formatTime(ferry?.departure)}</div>
                    <div className="text-xs text-muted-foreground">Départ</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-px bg-border" />
                    <Icon name="Ship" size={16} color="var(--color-muted-foreground)" />
                    <div className="w-8 h-px bg-border" />
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-foreground">{formatTime(ferry?.arrival)}</div>
                    <div className="text-xs text-muted-foreground">Arrivée</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name={getStatusIcon(ferry?.status)} size={14} className={getStatusColor(ferry?.status)} />
                    <span className={`text-xs font-medium ${getStatusColor(ferry?.status)}`}>
                      {ferry?.status === 'confirmed' ? 'Confirmé' : ferry?.status === 'delayed' ? 'Retardé' : 'Annulé'}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">{ferry?.company}</div>
                  <div className="text-sm text-muted-foreground">{ferry?.vessel}</div>
                  <div className="text-sm font-medium text-foreground">{ferry?.price} €</div>
                </div>
              </div>
            </div>
          );
        })}
        {apiErrors?.length > 0 && (
          <div className="mt-4 p-3 bg-warning/10 border border-warning/30 rounded-lg text-xs space-y-1">
            {apiErrors.map((err, i) => (
              <div key={i} className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={12} className="text-warning" />
                <span>
                  {err.company === 'yeu-continent' && 'Yeu Continent'}
                  {err.company === 'compagnie-vendeenne' && 'Compagnie Vendéenne'}
                  {err.company === 'all' && 'APIs ferries'} : {err.message}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {ferryError && (
        <div className="mt-4 p-3 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} color="var(--color-error)" />
            <span className="text-sm text-error">{ferryError}</span>
          </div>
        </div>
      )}

      <div className="mt-4 p-3 bg-muted rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium mb-1">Informations importantes :</p>
            <ul className="space-y-1">
              <li>• Les horaires peuvent changer selon les conditions météorologiques</li>
              <li>• Arrivée recommandée 30 minutes avant le départ</li>
              <li>• Les prix affichés sont pour un passager adulte</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FerryScheduleSelector;